/**
 * PostureTabs — the Command Center's view tabs: Posture · Operations · History,
 * as Carbon line tabs (blueprint §4.5 LineTabs, §5.2 cc).
 *
 * URL-synced via `?tab=posture|operations|history`. `posture` is the default
 * and elides from the URL: link to /dashboard and you land on Posture. The
 * selected tab comes from `useDashboardTab`, never from router matching.
 * Tab ids (`tab-{id}`) and the panels they control (`panel-{id}`) are the
 * Dashboard's existing ids.
 *
 * Right-side actions (commit, download, refresh) arrive in a slot so the
 * parent owns their handlers; they wrap to their own row on phones.
 */

import { useSearchParams } from 'react-router-dom'
import { LineTabs } from '@/components/carbon'

export type DashboardTab = 'posture' | 'operations' | 'history'

const TABS: ReadonlyArray<{ id: DashboardTab; label: string }> = [
  { id: 'posture', label: 'Posture' },
  { id: 'operations', label: 'Operations' },
  { id: 'history', label: 'History' },
]

interface Props {
  /** Right-side actions slot. */
  actions?: React.ReactNode
}

export function useDashboardTab(): [DashboardTab, (next: DashboardTab) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get('tab')
  const current: DashboardTab =
    raw === 'operations' ? 'operations' :
    raw === 'history'    ? 'history' :
                           'posture'

  const setTab = (next: DashboardTab) => {
    setParams(prev => {
      // Default tab elides from the URL; explicit choices persist. Preserves
      // any other search params the user has set.
      if (next === 'posture') prev.delete('tab')
      else prev.set('tab', next)
      return prev
    }, { replace: true })
  }

  return [current, setTab]
}

const isTab = (id: string): id is DashboardTab => TABS.some(t => t.id === id)

export default function PostureTabs({ actions }: Props) {
  const [current, setTab] = useDashboardTab()

  return (
    <div className="z-20 mt-12 bg-[color:var(--background)] c-md:sticky c-md:top-0">
      <LineTabs
        items={TABS}
        value={current}
        onChange={id => isTab(id) && setTab(id)}
        aria-label="Dashboard view"
        actions={actions}
      />
    </div>
  )
}
